/**
 * Match-level helpers for the Add-from-pool sheet.
 *
 * Maps a 0–100 overall match score to a 3-level qualitative tier and provides a
 * skill-intersection helper for highlighting a talent's skills that match the
 * job's required skills.
 *
 * Modelled on the social score tiers in CandidateInfo:
 * a (label key, Tag intent) pair chosen by threshold.
 */

import type { TagIntent } from '@/components/ui/atoms/Tag';
import { normalizeAccents } from '@/services/talentPool.utils';
import type { TalentSkill } from '@/types/talent.types';

export type MatchLevel = 'perfect' | 'good' | 'partial';

export interface MatchLevelTier {
  /** Qualitative level — drives the i18n label key talentPool.addFromPool.matchLevel.<level>. */
  level: MatchLevel;
  /** i18n key for the level label. */
  labelKey: string;
  /** Tag intent (Perfect = primary/purple, Good = info/blue, Partial = secondary). */
  intent: TagIntent;
}

/**
 * Map an overall match score to a 3-level qualitative tier.
 *   Perfect (>= 80)  → primary (purple)
 *   Good    (50–79)  → info (blue)
 *   Partial (< 50)   → secondary
 */
export function getMatchLevelTier(score: number): MatchLevelTier {
  if (score >= 80) {
    return { level: 'perfect', labelKey: 'addFromPool.matchLevel.perfect', intent: 'primary' };
  }
  if (score >= 50) {
    return { level: 'good', labelKey: 'addFromPool.matchLevel.good', intent: 'info' };
  }
  return { level: 'partial', labelKey: 'addFromPool.matchLevel.partial', intent: 'secondary' };
}

/**
 * Build the accent-insensitive set of a job's required skill names for fast lookup.
 */
export function buildRequiredSkillSet(requiredSkills: TalentSkill[]): Set<string> {
  return new Set(requiredSkills.map((s) => normalizeAccents(s.name.toLowerCase())));
}

/**
 * Decide whether a talent skill matches one of the job's required skills
 * (accent-insensitive). Empty required-set means nothing is highlighted.
 */
export function isSkillMatched(skillName: string, requiredSet: Set<string>): boolean {
  if (requiredSet.size === 0) return false;
  return requiredSet.has(normalizeAccents(skillName.toLowerCase()));
}
